"use client";

import type { ReactNode } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";

type AccentColor = "blue" | "green" | "red" | "amber";

const accentStyles: Record<AccentColor, { bg: string; text: string }> = {
  blue: { bg: "bg-sky-500/10", text: "text-sky-500" },
  green: { bg: "bg-emerald-500/10", text: "text-emerald-600" },
  red: { bg: "bg-red-500/10", text: "text-red-500" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-500" },
};

type SummaryCardProps = {
  title: string;
  value: string;
  icon: ReactNode;
  subtitle?: string;
  trend?: number;
  accentColor?: AccentColor;
};

export function SummaryCard({
  title,
  value,
  icon,
  subtitle,
  trend,
  accentColor = "blue",
}: SummaryCardProps) {
  const accent = accentStyles[accentColor];

  return (
    <div className="rounded-2xl border border-border  p-5 shadow-sm bg-blue-200">
      <div className="flex items-start justify-between gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent.bg} ${accent.text}`}>
          {icon}
        </div>
        {typeof trend === "number" ? (
          <div
            className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              trend >= 0 ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-500"
            }`}
          >
            {trend >= 0 ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
            {Math.abs(trend)}%
          </div>
        ) : null}
      </div>
      <div className="mt-4 space-y-1">
        <p className="text-3xl font-semibold tracking-tight text-black">{value}</p>
        <p className="text-sm text-green-700">{title}</p>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>
    </div>
  );
}
